/**
 * Reveal — Intersection Observer scroll-triggered animation.
 *
 * Wraps children and fades/slides them in when they enter the viewport.
 * Respects `prefers-reduced-motion` via CSS (no JS check needed).
 */

import { useEffect, useRef, type ReactNode, type ElementType } from "react";

type RevealProps = {
  children: ReactNode;
  /** Delay in ms before the reveal fires after intersection */
  delay?: number;
  /** HTML element to render as (default: div) */
  as?: ElementType;
  className?: string;
};

export function Reveal({
  children,
  delay = 0,
  as: Component = "div",
  className = "",
}: RevealProps) {
  const ref = useRef<HTMLDivElement>(null);
  // Holds the pending reveal timeout so it can be cleared on unmount.
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    // Eagerly show on mobile or reduced motion
    const prefersReducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const isMobile = window.innerWidth < 768;

    if (prefersReducedMotion || isMobile) {
      el.dataset.shown = "true";
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          const timeout = setTimeout(() => {
            el.dataset.shown = "true";
          }, delay);
          observer.unobserve(el);
          // Track the timeout so it can be cleared on unmount.
          timeoutRef.current = timeout;
        }
      },
      { threshold: 0.01, rootMargin: "200px 0px 200px 0px" },
    );

    observer.observe(el);

    // Fallback safety: ensure elements become visible even if observer misses
    timeoutRef.current = setTimeout(() => {
      if (el) el.dataset.shown = "true";
    }, 800);

    return () => {
      observer.disconnect();
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, [delay]);

  return (
    <Component ref={ref} className={`reveal ${className}`}>
      {children}
    </Component>
  );
}
